// universe-drawers-context (2026-11-04): /knowledge — "מה המערכת יודעת על העסק". המגירות (ישויות, כללים, אנשים ותפקידים, תהליכים,
// קשרים בין עולמות), כל פריט עם מי אמר, מתי, מי אישר, "מתייחס ל" ו"בשימוש ב". מסונן לצופה בשרת (GET /api/knowledge) — מה שמעוגן
// לעולם שהצופה לא רואה לא מגיע לכאן. קריאה בלבד: שינוי רק דרך אייבי וכרטיס אישור (נתיב כתיבה אחד).
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { SemanticDrawer } from '@aibify/core';
import { json } from '../lib/api.ts';
import { useDocTitle } from '../lib/brand.ts';
import { T } from '../lib/strings.ts';
import { Input } from '../components/ui/input.tsx';
import { EmptyState, ErrorView, Loading } from '../components/Status.tsx';
import { IconSearch } from '../components/brand/icons.tsx';

const Item = z.object({
  id: z.string(), textHe: z.string(), saidBy: z.string().nullable(), approvedBy: z.string().nullable(), createdAt: z.string(),
  anchorsHe: z.array(z.string()), usedInHe: z.string().nullable(),
});
const Knowledge = z.object({ drawers: z.array(z.object({ drawer: SemanticDrawer, nameHe: z.string(), items: z.array(Item) })), total: z.number() });

const day = (iso: string) => new Date(iso).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem', day: 'numeric', month: 'numeric', year: 'numeric' });

export function KnowledgePage() {
  useDocTitle(T.knowledge.page);
  const [text, setText] = useState('');
  const q = useQuery({ queryKey: ['knowledge'], queryFn: () => json(Knowledge, '/api/knowledge') });
  const needle = text.trim().toLowerCase();
  const drawers = (q.data?.drawers ?? [])
    .map((d) => ({ ...d, items: d.items.filter((i) => !needle || `${i.textHe} ${i.anchorsHe.join(' ')} ${i.saidBy ?? ''}`.toLowerCase().includes(needle)) }))
    .filter((d) => d.items.length);

  return (
    <section className="flex max-w-3xl flex-col gap-5" data-testid="knowledge-page">
      <header className="flex flex-col gap-1">
        <h1 className="page-title">{T.knowledge.page}</h1>
        <p className="m-0 text-sm text-muted-foreground">{T.knowledge.lead}</p>
      </header>
      {q.isError && <ErrorView error={q.error} onRetry={() => q.refetch()} />}
      {q.isPending && <Loading />}
      {q.data && q.data.total > 0 && (
        <div className="relative">
          <IconSearch size={18} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input type="search" className="ps-10" placeholder={T.knowledge.search} aria-label={T.knowledge.search} value={text} onChange={(e) => setText(e.target.value)} />
        </div>
      )}
      {q.data && !drawers.length && <EmptyState title={needle ? T.knowledge.noMatch : T.knowledge.empty}>{needle ? null : T.knowledge.emptyLead}</EmptyState>}
      {drawers.map((d) => (
        <section key={d.drawer} className="flex flex-col gap-2" data-testid={`drawer-${d.drawer}`}>
          <h2 className="m-0 text-base font-semibold">
            {d.nameHe} <span className="text-sm font-normal text-muted-foreground">({d.items.length})</span>
          </h2>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {d.items.map((i) => (
              <li key={i.id} className="flex flex-col gap-1.5 rounded-md border border-border bg-card p-4 shadow-card">
                <p className="m-0 text-[15px] leading-relaxed">{i.textHe}</p>
                <p className="m-0 text-xs text-muted-foreground">
                  {T.knowledge.said(i.saidBy ?? T.knowledge.unknown, day(i.createdAt))}
                  {i.approvedBy && i.approvedBy !== i.saidBy ? ` · ${T.knowledge.approved(i.approvedBy)}` : ''}
                </p>
                {(i.anchorsHe.length > 0 || i.usedInHe) && (
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    {i.anchorsHe.map((a) => (
                      <span key={a} className="rounded-sm bg-muted px-2 py-0.5 text-foreground">{a}</span>
                    ))}
                    {i.usedInHe && <span className="rounded-sm border border-border px-2 py-0.5 text-muted-foreground">{T.knowledge.usedIn(i.usedInHe)}</span>}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </section>
  );
}
